import {
  COLOR_TOKENS,
  SCORE_CATEGORIES,
  SCORE_CATEGORY_LABELS,
  type CreateScoringRuleBody,
  type ScoreCategory,
  type ScoringRuleAdminDTO,
  type ScoringRulesResponse,
  type TierInput,
  type TiersResponse,
  tierIssues,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, IconButton, Input, Select, Switch, useConfirm, useToast } from '../../../components/ui';
import { useLookups } from '../../../hooks/useMeta';
import { ApiError, api, errorMessage } from '../../../lib/api';

export function ScoringSection() {
  return (
    <>
      <RulesEditor />
      <TiersEditor />
    </>
  );
}

function RulesEditor() {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { meta } = useLookups();
  const { data } = useQuery({ queryKey: ['settings', 'scoring'], queryFn: ({ signal }) => api.get<ScoringRulesResponse>('/api/scoring/rules', { signal }) });
  const [draft, setDraft] = useState<{ kind: 'signal' | 'tag'; label: string; tagId: string; category: ScoreCategory; points: string }>({ kind: 'signal', label: '', tagId: '', category: 'engagement', points: '10' });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['settings', 'scoring'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const recalculating = () => toast.success('Saved — every lead score is being recalculated');
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/api/scoring/rules/${id}`, patch),
    onSuccess: async () => {
      recalculating();
      await refresh();
    },
    onError,
  });
  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/api/scoring/rules',
        (draft.kind === 'tag'
          ? { kind: 'tag', tagId: draft.tagId, category: draft.category, points: Number(draft.points) }
          : { kind: 'signal', label: draft.label, category: draft.category, points: Number(draft.points), decayDays: 365, maxOccurrences: 1 }) satisfies CreateScoringRuleBody,
      ),
    onSuccess: async () => {
      setDraft({ ...draft, label: '', tagId: '' });
      recalculating();
      await refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) => api.delete(`/api/scoring/rules/${id}`, { confirm: confirmed }),
    onSuccess: refresh,
    onError: async (error, variables) => {
      if (error instanceof ApiError && error.status === 409 && !variables.confirmed && typeof error.details === 'object' && error.details !== null && 'requiresConfirmation' in error.details) {
        if (await confirm({ title: 'Delete this rule?', message: error.message, confirmLabel: 'Delete', danger: true })) remove.mutate({ id: variables.id, confirmed: true });
        return;
      }
      onError(error);
    },
  });

  const rules = data?.rules ?? [];
  return (
    <Card>
      <CardHeader title="Scoring rules" description="Points add up to a 0–100 lead score. Each lead shows exactly which rules gave it points." />
      {SCORE_CATEGORIES.map((category) => {
        const inCategory = rules.filter((r) => r.category === category);
        if (inCategory.length === 0) return null;
        return (
          <div key={category}>
            <p className="bg-slate-50 px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">{SCORE_CATEGORY_LABELS[category]}</p>
            <ul className="divide-y divide-slate-100">
              {inCategory.map((rule) => (
                <RuleRow key={`${rule.id}:${rule.points}:${JSON.stringify(rule.params)}`} rule={rule} onSave={(patch) => update.mutate({ id: rule.id, patch })} onDelete={() => remove.mutate({ id: rule.id, confirmed: false })} />
              ))}
            </ul>
          </div>
        );
      })}
      <form
        className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3 text-[13px]"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Select size="sm" aria-label="Rule type" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value === 'tag' ? 'tag' : 'signal' })}>
          <option value="signal">New signal (logged on a lead)</option>
          <option value="tag">Points for a tag</option>
        </Select>
        {draft.kind === 'signal' ? (
          <Input size="sm" className="w-56" aria-label="Signal name" placeholder="Attended our open house" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
        ) : (
          <Select size="sm" aria-label="Tag" value={draft.tagId} onChange={(e) => setDraft({ ...draft, tagId: e.target.value })} placeholder="Choose a tag">
            {(meta?.tags ?? []).map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
        )}
        <Select size="sm" aria-label="Category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: SCORE_CATEGORIES.find((c) => c === e.target.value) ?? 'engagement' })}>
          {SCORE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {SCORE_CATEGORY_LABELS[c]}
            </option>
          ))}
        </Select>
        <Input size="sm" className="w-20" type="number" min={-100} max={100} aria-label="Points" value={draft.points} onChange={(e) => setDraft({ ...draft, points: e.target.value })} />
        <span className="text-slate-500">points</span>
        <Button size="sm" type="submit" icon={Plus} loading={create.isPending} disabled={draft.kind === 'signal' ? !draft.label.trim() : !draft.tagId}>
          Add rule
        </Button>
      </form>
    </Card>
  );
}

function RuleRow({ rule, onSave, onDelete }: { rule: ScoringRuleAdminDTO; onSave: (patch: Record<string, unknown>) => void; onDelete: () => void }) {
  const [points, setPoints] = useState(String(rule.points));
  const [params, setParams] = useState<Record<string, unknown>>(rule.params);
  const numericParams = Object.entries(rule.builtIn?.params ?? {}).filter(([, meta]) => typeof meta.default === 'number');
  const changed = Number(points) !== rule.points || JSON.stringify(params) !== JSON.stringify(rule.params);
  return (
    <li className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
      <Switch size="sm" checked={rule.isEnabled} onChange={(isEnabled) => onSave({ isEnabled })} aria-label={`Enable ${rule.label}`} />
      <div className="min-w-56 flex-1">
        <p className={rule.isEnabled ? 'text-slate-900' : 'text-slate-400'}>
          {rule.label} {rule.kind !== 'attribute' && <Badge size="sm">{rule.kind}</Badge>}
        </p>
        <p className="text-xs text-slate-500">
          {rule.builtIn?.description ?? rule.description}
          {rule.kind === 'signal' && ` · ${rule.signalCount} active · counts for ${rule.decayDays ?? '∞'} days${rule.maxOccurrences > 1 ? `, up to ${rule.maxOccurrences}×` : ''}`}
        </p>
      </div>
      {numericParams.map(([key, meta]) => (
        <label key={key} className="flex items-center gap-1 text-xs text-slate-500">
          {meta.label}
          <Input size="sm" className="w-16" type="number" min={1} value={typeof params[key] === 'number' ? String(params[key]) : ''} onChange={(e) => setParams({ ...params, [key]: Number(e.target.value) })} />
        </label>
      ))}
      <Input size="sm" className="w-20" type="number" min={-100} max={100} aria-label={`Points for ${rule.label}`} value={points} onChange={(e) => setPoints(e.target.value)} />
      <span className="text-xs text-slate-500">pts</span>
      <IconButton
        icon={Save}
        label={`Save ${rule.label}`}
        size="sm"
        variant={changed ? 'primary' : 'ghost'}
        disabled={!changed}
        onClick={() => onSave({ points: Number(points), ...(JSON.stringify(params) !== JSON.stringify(rule.params) ? { params } : {}) })}
      />
      <IconButton icon={Trash2} label={`Delete ${rule.label}`} size="sm" variant="ghost" disabled={rule.isSystem} onClick={onDelete} />
    </li>
  );
}

function TiersEditor() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings', 'tiers'], queryFn: ({ signal }) => api.get<TiersResponse>('/api/scoring/tiers', { signal }) });
  if (!data) return null;
  return <TiersForm key={JSON.stringify(data.tiers)} initial={data.tiers} onSaved={async () => {
    toast.success('Tiers saved — leads are being re-tiered');
    await queryClient.invalidateQueries({ queryKey: ['settings', 'tiers'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  }} />;
}

function TiersForm({ initial, onSaved }: { initial: TiersResponse['tiers']; onSaved: () => Promise<void> }) {
  const toast = useToast();
  const [tiers, setTiers] = useState<(TierInput & { isSystem: boolean; leadCount: number })[]>(initial.map((t) => ({ ...t, color: COLOR_TOKENS.find((c) => c === t.color) ?? 'slate', description: t.description ?? null })));
  const issues = tierIssues(tiers);
  const save = useMutation({
    mutationFn: () => api.put('/api/scoring/tiers', { tiers: tiers.map(({ key, label, minScore, color, description }) => ({ key, label, minScore, color, description })) }),
    onSuccess: onSaved,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const set = (index: number, patch: Partial<TierInput>) => setTiers(tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  return (
    <Card>
      <CardHeader title="Tiers" description="A score falls in the highest tier whose minimum it reaches." />
      <CardBody className="space-y-2 text-[13px]">
        {tiers.map((tier, index) => (
          <div key={tier.key} className="flex flex-wrap items-center gap-2">
            <Input size="sm" className="w-56" aria-label="Tier label" value={tier.label} onChange={(e) => set(index, { label: e.target.value })} />
            <span className="text-xs text-slate-500">from</span>
            <Input size="sm" className="w-20" type="number" min={0} max={100} aria-label={`Minimum score for ${tier.label}`} value={tier.minScore} onChange={(e) => set(index, { minScore: Number(e.target.value) })} />
            <Select size="sm" aria-label="Color" value={tier.color} onChange={(e) => set(index, { color: COLOR_TOKENS.find((c) => c === e.target.value) ?? 'slate' })}>
              {COLOR_TOKENS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <span className="text-xs text-slate-500">{tier.leadCount} leads</span>
            {!tier.isSystem && <IconButton icon={Trash2} label={`Remove ${tier.label}`} size="sm" variant="ghost" onClick={() => setTiers(tiers.filter((_, i) => i !== index))} />}
          </div>
        ))}
        {issues.map((issue) => (
          <p key={`${issue.path}:${issue.message}`} className="text-red-700">
            {issue.message}
          </p>
        ))}
        <Button variant="primary" icon={Save} disabled={issues.length > 0} loading={save.isPending} onClick={() => save.mutate()}>
          Save tiers
        </Button>
      </CardBody>
    </Card>
  );
}
